/**
 * Dashboard slicers: what a viewer has picked, and where the options come from.
 *
 * Two things, kept apart on purpose. The **definition** - which column, shown
 * how - is part of the dashboard document, a `slicer` tile you place and size
 * like a chart (`dashboard-state.ts`), so it is authored in Edit mode, saved
 * with Save and shared with the dashboard. The **selection** is the viewer's
 * own: it narrows what *they* see, is never written into the shared document,
 * and so never makes the dashboard unsaved and works when it is view-only.
 *
 * A selection is two-stage. A slicer tile edits a draft; Apply commits it, and
 * only a commit refetches charts (the app never re-queries silently mid-pick).
 *
 * Selections live in one atom keyed by dashboard and tile. Where they are
 * remembered between visits is meant to change only in this file.
 */

import { Atom } from '../../state/atom.ts';
import { getColumns, getColumnProfile, getColumnValues, type ColumnProfile } from '../../api/endpoints.ts';
import { isTemporalType } from '../../utils/columns.ts';
import type { ColumnInfo } from '../../api/types.ts';
import type { Dashboard, DashboardTab, DashboardTile } from './dashboard-state.ts';
import { modeFits, type ActiveSlicer, type SlicerDef, type SlicerFamily } from './variables.ts';
import { buildPool, type PoolColumn } from '../ship/ship-columns.ts';
import { fmtTimeShort } from '../plot/render/format.ts';

/**
 * The most values a list or dropdown loads. Past this the list says it is
 * showing the first ones and the search box is how to reach the rest.
 */
export const MAX_SLICER_VALUES = 5000;

/** What a viewer has applied to one slicer: values, or a window. */
export interface SlicerSelection {
  values?: string[];
  min?: number | null;
  max?: number | null;
}

/** `dashboardId:tileId` -> the selection applied. Absent: nothing narrowed. */
export const slicerSelections = new Atom<Record<string, SlicerSelection>>({});

const key = (dashboardId: string, tileId: string): string => `${dashboardId}:${tileId}`;

/** The slicer tiles on a tab, in the order they were added. */
export function slicerTiles(tab: Pick<DashboardTab, 'tiles'> | null | undefined): DashboardTile[] {
  return (tab?.tiles ?? []).filter((t) => t.kind === 'slicer' && !!t.slicer);
}

export function selectionOf(dashboardId: string, tileId: string): SlicerSelection {
  return slicerSelections.value[key(dashboardId, tileId)] ?? {};
}

/** Whether a selection cuts anything. An untouched slicer is a no-op. */
export function narrows(sel: SlicerSelection | undefined): boolean {
  return !!sel && ((sel.values?.length ?? 0) > 0 || sel.min != null || sel.max != null);
}

/** Commit a selection. `null`, or one that cuts nothing, clears the slicer. */
export function applySelection(dashboardId: string, tileId: string, sel: SlicerSelection | null): void {
  const next = { ...slicerSelections.value };
  if (sel && narrows(sel)) next[key(dashboardId, tileId)] = { ...sel };
  else delete next[key(dashboardId, tileId)];
  slicerSelections.set(next);
  dismissSlicerNote(dashboardId, tileId);
}

/**
 * What a slicer says after the dashboard's time moved under its selection -
 * shown on the slicer until the viewer picks again or closes it.
 */
export const slicerNotes = new Atom<Record<string, string>>({});

export function slicerNoteOf(dashboardId: string, tileId: string): string | null {
  return slicerNotes.value[key(dashboardId, tileId)] ?? null;
}

export function dismissSlicerNote(dashboardId: string, tileId: string): void {
  const k = key(dashboardId, tileId);
  if (!(k in slicerNotes.value)) return;
  const next = { ...slicerNotes.value };
  delete next[k];
  slicerNotes.set(next);
}

/** A window end (`YYYY-MM-DDTHH:MM:SS`, UTC) in epoch seconds, as time slicers count. */
function utcSeconds(value: string): number {
  return Date.parse(`${value.replace(' ', 'T')}Z`) / 1000;
}

/**
 * Keep time slicers inside the dashboard's time, after it changed.
 *
 * A time slicer's range is drawn over the dashboard's window, but what it
 * selects is fixed times. When the window moves, a selection can end up
 * partly or wholly outside it, and every chart then asks for a window and a
 * filter that do not meet - empty, with nothing to say why. So a selection is
 * cut to the new window; one that no longer meets it, or that now covers all
 * of it, is cleared. Each slicer that changed says so (`slicerNotes`).
 */
export function trimTimeSelections(dashboard: Dashboard, start: string, end: string): void {
  const ws = utcSeconds(start);
  const we = utcSeconds(end);
  if (!Number.isFinite(ws) || !Number.isFinite(we)) return;
  const selections = { ...slicerSelections.value };
  const notes = { ...slicerNotes.value };
  let changed = false;
  for (const tab of dashboard.tabs) {
    for (const tile of slicerTiles(tab)) {
      if (tile.slicer!.family !== 'time') continue;
      const k = key(dashboard.id, tile.id);
      const sel = selections[k];
      if (!sel || sel.values?.length || !narrows(sel)) continue;
      const lo = sel.min ?? null;
      const hi = sel.max ?? null;
      if ((hi != null && hi < ws) || (lo != null && lo > we)) {
        const range = `${lo != null ? fmtTimeShort(lo) : 'the start'} - ${hi != null ? fmtTimeShort(hi) : 'the end'}`;
        delete selections[k];
        notes[k] = `Filter cleared: ${range} is outside the dashboard's time.`;
        changed = true;
        continue;
      }
      // An end at or past the window's edge is the edge: open, as the slider stores it.
      const nlo = lo != null && lo > ws ? lo : null;
      const nhi = hi != null && hi < we ? hi : null;
      if (nlo === lo && nhi === hi) continue;
      if (nlo == null && nhi == null) {
        delete selections[k];
        notes[k] = "Filter cleared: the dashboard's time now lies inside it.";
      } else {
        selections[k] = { min: nlo, max: nhi };
        notes[k] = "Filter trimmed to the dashboard's time.";
      }
      changed = true;
    }
  }
  if (!changed) return;
  slicerSelections.set(selections);
  slicerNotes.set(notes);
}

export function clearAllSelections(dashboard: Dashboard, tab: DashboardTab): void {
  const next = { ...slicerSelections.value };
  for (const t of slicerTiles(tab)) delete next[key(dashboard.id, t.id)];
  slicerSelections.set(next);
}

/** The tab's slicers that currently narrow something - what charts are resolved with. */
export function activeSlicers(dashboard: Dashboard | null, tab: DashboardTab | null): ActiveSlicer[] {
  if (!dashboard || !tab) return [];
  const out: ActiveSlicer[] = [];
  for (const tile of slicerTiles(tab)) {
    const def = tile.slicer!;
    const sel = selectionOf(dashboard.id, tile.id);
    if (def.mode === 'list' || def.mode === 'dropdown') {
      if (sel.values?.length) out.push({ id: tile.id, column: def.column, values: sel.values });
    } else if (sel.min != null || sel.max != null) {
      out.push({
        id: tile.id,
        column: def.column,
        min: sel.min ?? null,
        max: sel.max ?? null,
        temporal: def.family === 'time',
      });
    }
  }
  return out;
}

/* ── Columns ────────────────────────────────────────────────────── */

/** Which family a column belongs to, from its declared type. */
export function familyOf(type: string): SlicerFamily {
  if (isTemporalType(type)) return 'time';
  if (/DOUBLE|FLOAT|REAL|DECIMAL/i.test(type)) return 'float';
  if (/INT/i.test(type)) return 'int';
  return 'text';
}

const columnCache = new Map<string, Promise<ColumnInfo[]>>();

/** A ship's columns, once - the picker lists them and reads their types. */
export function shipColumns(ship: string): Promise<ColumnInfo[]> {
  let hit = columnCache.get(ship);
  if (!hit) {
    hit = getColumns(ship);
    columnCache.set(ship, hit);
    hit.catch(() => columnCache.delete(ship));
  }
  return hit;
}

/**
 * The columns a filter can be made on: every column any of these ships reports,
 * merged the way Explore merges them (`buildPool`), so the picker is Explore's.
 */
export async function filterColumnPool(ships: string[]): Promise<PoolColumn[]> {
  const byShip = new Map<string, ColumnInfo[]>();
  await Promise.all(
    ships.map(async (s) => {
      byShip.set(s, await shipColumns(s).catch(() => []));
    }),
  );
  return buildPool(ships, byShip);
}

/**
 * Whether a slicer can be made as asked, or why not: the right kind of column
 * (which the dialog already offers), and for a list or dropdown, some values.
 * A column with thousands of values is allowed - the list gets a search box.
 */
export async function checkSlicer(
  ships: string[],
  def: SlicerDef,
): Promise<{ ok: true } | { ok: false; reason: string }> {
  if (!modeFits(def.mode, def.family)) {
    return { ok: false, reason: `${def.column} cannot be shown that way.` };
  }
  if (def.mode !== 'list' && def.mode !== 'dropdown') return { ok: true };
  const options = await slicerOptions(ships, def.column).catch(() => null);
  if (options?.kind === 'ready') return { ok: true };
  if (options?.kind === 'empty') return { ok: false, reason: `${def.column} has no values for the ships on screen.` };
  return { ok: false, reason: `Could not read the values of ${def.column}. Try again.` };
}

/* ── Options and profiles ───────────────────────────────────────── */

export type SlicerOptions =
  /** `truncated`: the column has more values than were loaded. */
  | { kind: 'ready'; values: string[]; truncated: boolean }
  | { kind: 'empty' };

const optionCache = new Map<string, Promise<SlicerOptions>>();

/**
 * The values a list offers: the union across the ships on screen, so a
 * dashboard of several ships offers everything any of them has.
 *
 * Cached per ship and column for the page's life. Reference data like a mode
 * list changes rarely, and a slicer that refetched on every render would cost a
 * query each.
 */
export function slicerOptions(ships: string[], column: string): Promise<SlicerOptions> {
  const id = `${[...ships].sort().join(',')}|${column}`;
  let hit = optionCache.get(id);
  if (!hit) {
    hit = loadOptions(ships, column);
    optionCache.set(id, hit);
    // A failure is not cached: the next look should try again.
    hit.catch(() => optionCache.delete(id));
  }
  return hit;
}

async function loadOptions(ships: string[], column: string): Promise<SlicerOptions> {
  const lists = await Promise.all(
    ships.map((s) => getColumnValues(s, column, MAX_SLICER_VALUES + 1)),
  );
  const seen = new Set<string>();
  // The server stops at the limit, so a full list means "at least this many".
  let truncated = false;
  for (const list of lists) {
    if (list.length > MAX_SLICER_VALUES) truncated = true;
    for (const v of list.slice(0, MAX_SLICER_VALUES)) seen.add(String(v));
  }
  if (!seen.size) return { kind: 'empty' };
  const values = [...seen].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  return {
    kind: 'ready',
    values: values.slice(0, MAX_SLICER_VALUES),
    truncated: truncated || values.length > MAX_SLICER_VALUES,
  };
}

const windowValueCache = new Map<string, Promise<Set<string> | null>>();

/**
 * The values read in the dashboard's window, across the ships on screen. A
 * list keeps every value, so a pick never vanishes when the time moves, and
 * dims the ones outside this set: picking one would leave the charts empty.
 * Null when the window holds more values than are loaded - then nothing can
 * be said about the rest, and nothing is dimmed.
 */
export function slicerWindowValues(
  ships: string[],
  column: string,
  start: string,
  end: string,
): Promise<Set<string> | null> {
  const id = `${[...ships].sort().join(',')}|${column}|${start}|${end}`;
  let hit = windowValueCache.get(id);
  if (!hit) {
    hit = Promise.all(
      ships.map((s) => getColumnValues(s, column, MAX_SLICER_VALUES + 1, undefined, { start, end })),
    ).then((lists) => {
      if (lists.some((list) => list.length > MAX_SLICER_VALUES)) return null;
      return new Set(lists.flat().map(String));
    });
    windowValueCache.set(id, hit);
    hit.catch(() => windowValueCache.delete(id));
  }
  return hit;
}

const profileCache = new Map<string, Promise<ColumnProfile>>();

/** The extent and histogram a range slicer draws, for these ships and this window. */
export function slicerProfile(
  ships: string[],
  column: string,
  start: string,
  end: string,
): Promise<ColumnProfile> {
  const id = `${[...ships].sort().join(',')}|${column}|${start}|${end}`;
  let hit = profileCache.get(id);
  if (!hit) {
    hit = getColumnProfile(ships, column, start, end);
    profileCache.set(id, hit);
    hit.catch(() => profileCache.delete(id));
  }
  return hit;
}
